// Wave 3 — Public-site shell: header + footer around every unauth page.
// Light-refined executive theme scoped to `.erf-public` class so it never
// leaks into the operator app.
import React, { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { api } from "../../lib/api";
import Wordmark from "./Wordmark";
import { Menu, X } from "lucide-react";

const NAV = [
  { to: "/", label: "Home", end: true },
  { to: "/services", label: "Services" },
  { to: "/portfolio", label: "Portfolio" },
  { to: "/about", label: "About" },
  { to: "/service-areas", label: "Service Areas" },
  { to: "/contact", label: "Contact" },
];

let _CONTENT_CACHE = null;
export function usePublicContent() {
  const [content, setContent] = useState(_CONTENT_CACHE || {});
  useEffect(() => {
    if (_CONTENT_CACHE) return;
    api.get("/public/website/content").then((r) => {
      _CONTENT_CACHE = r.data;
      setContent(r.data);
    }).catch(() => setContent({}));
  }, []);
  return content;
}

export default function PublicShell({ children, hero = null }) {
  const [open, setOpen] = useState(false);
  const content = usePublicContent();

  // Owner-locked UX (Icon Split + Focus-Ring pass):
  //   HEADER wordmark ALWAYS navigates to `/` (home).
  //   FOOTER wordmark ALWAYS performs browser-back with `/` fallback.
  // Both handlers must be invoked from click AND keyboard so mouse and
  // keyboard remain in lockstep. `outline` is inherited from the global
  // `*:focus-visible` rule in index.css (2px solid #B8892B, offset 2px);
  // we DO NOT set `outline: none` inline — that would defeat the focus
  // ring for both mouse and keyboard users.
  const goHome = () => { window.location.href = "/"; };
  const goBackWithHomeFallback = () => {
    if (typeof window !== "undefined" && window.history && window.history.length > 1) {
      window.history.back();
    } else {
      window.location.href = "/";
    }
  };
  const onHeaderClick = (e) => { e.preventDefault(); goHome(); };
  const onHeaderKey = (e) => {
    if (e.key === "Enter" || e.key === " ") { e.preventDefault(); goHome(); }
  };
  const onFooterClick = (e) => { e.preventDefault(); goBackWithHomeFallback(); };
  const onFooterKey = (e) => {
    if (e.key === "Enter" || e.key === " ") { e.preventDefault(); goBackWithHomeFallback(); }
  };

  return (
    <div className="erf-public" data-testid="erf-public-shell">
      <a href="#main-content" className="skip-link">Skip to main content</a>
      <header className="erf-public-header" data-testid="public-header">
        <div className="erf-public-container erf-public-header-inner">
          {/* Header wordmark → always home. */}
          <a
            href="/"
            role="link"
            tabIndex={0}
            aria-label="ERF Enterprises — home"
            data-testid="header-wordmark"
            onClick={onHeaderClick}
            onKeyDown={onHeaderKey}
            style={{ display: "inline-flex", cursor: "pointer" }}
            className="erf-public-wordmark-link"
          >
            <Wordmark tone="dark" size="md" />
          </a>
          <nav className="erf-public-nav" aria-label="Primary" data-testid="public-nav-desktop">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.end}
                className={({ isActive }) => `erf-public-nav-link ${isActive ? "is-active" : ""}`}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}>
                {n.label}
              </NavLink>
            ))}
            <Link
              to="/login"
              className="erf-public-nav-link erf-public-owner-login"
              data-testid="header-owner-login"
              style={{
                fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase",
                opacity: 0.72, marginLeft: 4,
              }}
            >
              Owner Login
            </Link>
            <Link to="/quote" className="erf-public-cta" data-testid="header-cta-quote">Request a Quote</Link>
          </nav>
          <button
            className="erf-public-menu-toggle"
            aria-label="Toggle menu"
            onClick={() => setOpen((o) => !o)}
            data-testid="mobile-menu-toggle"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
        {open && (
          <nav className="erf-public-nav-mobile" aria-label="Mobile" data-testid="public-nav-mobile">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.end} onClick={() => setOpen(false)}
                className={({ isActive }) => `erf-public-nav-link ${isActive ? "is-active" : ""}`}>
                {n.label}
              </NavLink>
            ))}
            <div
              style={{
                marginTop: 12, paddingTop: 12,
                borderTop: "1px solid rgba(15,15,16,0.08)",
                fontSize: 10, letterSpacing: "0.18em", textTransform: "uppercase",
                opacity: 0.55,
              }}
              aria-hidden="true"
            >
              Owner Access
            </div>
            <Link
              to="/login"
              onClick={() => setOpen(false)}
              className="erf-public-nav-link"
              data-testid="mobile-owner-login"
              style={{ fontSize: 12, letterSpacing: "0.1em", textTransform: "uppercase" }}
            >
              Owner Login
            </Link>
            <Link to="/quote" className="erf-public-cta" onClick={() => setOpen(false)}>Request a Quote</Link>
          </nav>
        )}
      </header>

      {hero}

      <main id="main-content" className="erf-public-main" data-testid="public-main">
        {children}
      </main>

      <footer className="erf-public-footer" data-testid="public-footer">
        <div className="erf-public-container erf-public-footer-inner">
          <div>
            {/* Footer wordmark → browser-back with home fallback. Simple
                history.length gate per Owner-locked spec (no referrer
                check — that caused mouse-click to fall through to `/`
                whenever the user landed via direct URL). */}
            <a
              href="/"
              role="link"
              tabIndex={0}
              aria-label="ERF Enterprises — go back to previous page"
              data-testid="footer-wordmark"
              onClick={onFooterClick}
              onKeyDown={onFooterKey}
              style={{ display: "inline-flex", cursor: "pointer" }}
              className="erf-public-wordmark-link erf-public-footer-wordmark"
            >
              <Wordmark tone="light" size="md" />
            </a>
            <p className="erf-public-footer-tag">
              {content.tagline || "From Foundation to Finish — Built to Last."}
            </p>
            {/* Marketing-Readiness Pass — licensing/registration block hidden.
                Do NOT re-enable without an authoritative license record. */}
          </div>
          <div>
            <h4 className="erf-public-footer-h4">Contact</h4>
            <ul>
              <li>Office: {content.contact_phone_office || "(800) 240-6404"}</li>
              <li>Mobile: {content.contact_phone_mobile || "(771) 271-3011"}</li>
              {content.contact_email && <li>Email: {content.contact_email}</li>}
            </ul>
          </div>
          <div>
            <h4 className="erf-public-footer-h4">Quick links</h4>
            <ul>
              <li><Link to="/services">Services</Link></li>
              <li><Link to="/portfolio">Portfolio</Link></li>
              <li><Link to="/quote">Request a quote</Link></li>
              <li><Link to="/client-login">Client / Team login</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="erf-public-footer-h4">Legal</h4>
            <ul>
              {/* R1e — canonical Twilio A2P-compliant routes. */}
              <li><Link to="/terms-and-conditions" data-testid="footer-terms-link">Terms &amp; Conditions</Link></li>
              <li><Link to="/privacy-policy" data-testid="footer-privacy-link">Privacy Policy</Link></li>
              <li><Link to="/login" data-testid="footer-owner-login">Owner Login</Link></li>
            </ul>
          </div>
        </div>
        <div className="erf-public-container erf-public-footer-copyright">
          {content.footer_copyright || `© ${new Date().getFullYear()} ERF ENTERPRISES LLC. All rights reserved.`}
        </div>
      </footer>
    </div>
  );
}


// Service tile — single professional image per slug (R1d.5). Uses the
// bundled image at /website/capabilities/<slug>.jpg if present; falls
// back to a deterministic gradient panel with the service name.
export function CapabilityTile({ service, height = 220, alt = "", showCaption = false }) {
  const slug = service?.slug || "service";
  const [imgOk, setImgOk] = React.useState(true);
  const bundledPath = service?.image_path || `/website/capabilities/${slug}.jpg`;
  // Deterministic hue from slug hash so each service has a stable palette.
  let h = 0;
  for (let i = 0; i < slug.length; i++) h = (h * 31 + slug.charCodeAt(i)) >>> 0;
  const hue1 = h % 360;
  const hue2 = (hue1 + 40) % 360;
  const gid = `g-${slug}`;
  const altText = alt || (service?.name ? `${service.name} — ERF ENTERPRISES LLC` : "ERF ENTERPRISES LLC service");

  return (
    <div className="erf-public-cap-tile" data-testid={`cap-tile-${slug}`} style={{ height, position: "relative" }}>
      {imgOk ? (
        <img
          src={bundledPath}
          alt={altText}
          loading="lazy"
          onError={() => setImgOk(false)}
          style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
        />
      ) : (
        <svg viewBox="0 0 400 260" preserveAspectRatio="xMidYMid slice" width="100%" height="100%" role="img" aria-label={altText}>
          <defs>
            <linearGradient id={gid} x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor={`hsl(${hue1}, 30%, 32%)`} />
              <stop offset="100%" stopColor={`hsl(${hue2}, 35%, 22%)`} />
            </linearGradient>
          </defs>
          <rect width="400" height="260" fill={`url(#${gid})`} />
          <rect x="0" y="215" width="400" height="45" fill="rgba(0,0,0,0.35)" />
          <text x="20" y="245" fill="#FAF7F2" fontFamily='"Playfair Display", serif' fontSize="20" fontWeight="600">
            {service?.name || "Service"}
          </text>
        </svg>
      )}
      {showCaption && service?.short_description && (
        <div style={{
          position: "absolute", bottom: 0, left: 0, right: 0,
          background: "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.55) 80%)",
          padding: "40px 16px 12px",
          pointerEvents: "none",
        }} aria-hidden="true">
          <div style={{ fontSize: 11, color: "rgba(250,247,242,0.85)", marginTop: 4 }}>
            {service.short_description}
          </div>
        </div>
      )}
    </div>
  );
}
